<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import ButtonFilter from "@/components/common/button/ButtonFilter.vue";
import EntityForm from "@/components/common/Form.vue";
import { useRoute } from "vue-router";

const route = useRoute();
const { t } = useI18n();

const props = defineProps({
    entity: {
        type: Object,
        required: true
    },
    label: {
        type: String,
        default: null
    },
    fieldsToUse: {
        type: String,
        default: null
    }
});

const emit = defineEmits(["filter", "clean", "search"]);

const filterValues = ref({
    page: 1
});

const appliedFilters = ref({});
const formData = ref([]);


const advancedFields = computed(() => {
    if (props.fieldsToUse && props.entity?.[props.fieldsToUse]) {
        return props.entity?.[props.fieldsToUse];
    }
    return props.entity?.pages?.list?.filters?.advanced?.fields || [];
});

function resetValues() {
    const newValues = {};

    advancedFields.value.forEach(field => {
        newValues[field.key] = field.default ?? "";
    });

    filterValues.value = {
        page: filterValues.value.page ?? 1,
        ...newValues
    };

    appliedFilters.value = {};
}

function applyFilter(isActive) {
    isActive.value = false;

    const sanitizedFormData = { ...formData.value };

    advancedFields.value.forEach(field => {
        if (field.remove && sanitizedFormData[field.key]) {
            sanitizedFormData[field.key] = sanitizeQuery(
                sanitizedFormData[field.key],
                field.remove
            );
        }
    });

    appliedFilters.value = {
        ...filterValues.value,
        ...sanitizedFormData
    };

    emit("filter", {
        ...filterValues.value,
        ...sanitizedFormData
    });
}

function cleanFilters() {
    resetValues();
    formData.value = [];
    emit("clean");
}

const activeFiltersCount = computed(() => {
    return Object.values(appliedFilters.value).filter(
        (v) => v !== "" && v !== null && v !== undefined
    ).length - 1;
});

function sanitizeQuery(query, removeList = []) {
    if (!query) return query;

    let sanitized = query;

    removeList.forEach(char => {
        const escaped = char.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // escapa regex corretamente
        const regex = new RegExp(escaped, "g");
        sanitized = sanitized.replace(regex, "");
    });

    return sanitized;
}

function handleSearch(payload) {
    // 🔥 descobre dinamicamente de onde vem o valor
    const field = payload.field || payload;
    const query = payload.query ?? field?.query ?? "";

    const sanitizedQuery = sanitizeQuery(query, field?.remove || []);

    emit("search", {
        handler: field.handler,
        query: sanitizedQuery
    });
}

function applyRouteParams() {
    const routeParams = route.query;

    if (!advancedFields.value.length) return;

    advancedFields.value.forEach(field => {
        const key = field.key;

        if (routeParams[key] !== undefined) {
            filterValues.value[key] = routeParams[key];
        } else {
            filterValues.value[key] = field.default ?? "";
        }
    });
}

watch(filterValues, () => {}, { deep: true });

onMounted(() => {
    applyRouteParams();
});
</script>

<template>
    <v-dialog max-width="550">
        <template v-slot:activator="{ props: activatorProps }">
            <v-badge
                v-if="activeFiltersCount > 0"
                :content="activeFiltersCount"
                color="error"
                offset-x="8"
                offset-y="8"
            >
                <ButtonFilter
                    :activator-props="activatorProps"
                    icon="add"
                    :label="label ? t(label) : t('common.labels.advancedFilters')"
                />
            </v-badge>

            <ButtonFilter
                v-else
                :activator-props="activatorProps"
                icon="add"
                :label="label ? t(label) : t('common.labels.advancedFilters')"
            />
        </template>

        <template v-slot:default="{ isActive }">
            <div class="create-dialog-box border-radius d-block bg-white border-0 shadow-none">
                <div class="title d-flex align-items-center justify-space-between">
                    <h5 class="mb-0">
                        {{ label ? t(label) : t('common.labels.advancedFilters') }}
                    </h5>

                    <button
                        class="close-btn bg-transparent p-0 border-none"
                        @click="isActive.value = false"
                    >
                        <i class="ri-close-fill"></i>
                    </button>
                </div>
                <form>
                    <EntityForm
                        :advancedFilter="true"
                        :entity="entity"
                        :fields="advancedFields"
                        :fieldsToUse="fieldsToUse"
                        ref="form"
                        v-model="formData"
                        @search="handleSearch"
                    />

                    <div class="btn-box">
                        <v-btn
                            class="info"
                            @click="cleanFilters"
                        >
                            {{ t('common.labels.toClean') }}
                        </v-btn>

                        <v-btn
                            color="success"
                            @click="applyFilter(isActive)"
                        >
                            {{ t('common.labels.apply') }}
                        </v-btn>
                    </div>
                </form>
            </div>
        </template>
    </v-dialog>
</template>
